import React from "react";
import { HomeOutlined, UserOutlined, SearchOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Select, Table, Button, Space, notification } from "antd";
import { useNavigate } from "react-router-dom";
import type { TableColumnType } from "antd";
import useSWR, { mutate } from "swr";
import axios from "axios";
import "./data_invoice_return_work_order.css";
import { statusMapping } from "./hoc_invoice_return_work_order";
import { useAuth } from "../../../context/AuthContext";


interface InvoiceReturnData {
    id: number;
    status: string;
    invoice_return_no: string;
    date_return: string;
    work_order_no: string;
    invoice_work_order_no: string;
    customer: string;
    chassis_no: string;
}

const DataInvoiceReturnWorkOrder: React.FC = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const fetcher = (url: string) => axios.get(url).then((res) => res.data);
    const { data, isLoading } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/data-list`, fetcher);

    const handleApprove = async (invoiceReturnNo: string) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/approve/${invoiceReturnNo}`);
            notification.success({
                message: 'Approval Berhasil',
                description: `Invoice ${invoiceReturnNo} telah disetujui dan stok sparepart telah diperbarui.`,
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
            });
            mutate(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-return/data-list`);
        } catch (error) {
            notification.error({
                message: 'Approval Gagal',
                description: 'Terjadi kesalahan saat memproses approval atau update stok.',
            });
            console.error("Approval failed", error);
        }
    };


    const getColumnSearchProps = (dataIndex: keyof InvoiceReturnData): TableColumnType<InvoiceReturnData> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes((value as string).toLowerCase()) : false,
    });


    const columns: TableColumnType<InvoiceReturnData>[] = [
        {
            title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status.localeCompare(b.status), ...getColumnSearchProps('status'),
            render: (status: any) => statusMapping[status] || status
        },
        { title: 'Invoice Return', dataIndex: 'invoice_return_no', key: 'invoice_return_no', defaultSortOrder: 'descend', sorter: (a, b) => a.invoice_return_no.localeCompare(b.invoice_return_no), ...getColumnSearchProps('invoice_return_no') },
        { title: 'Date Return', dataIndex: 'date_return', key: 'date_return', sorter: (a, b) => a.date_return.localeCompare(b.date_return), ...getColumnSearchProps('date_return') },
        { title: 'Work Order No', dataIndex: 'work_order_no', key: 'work_order_no', sorter: (a, b) => a.work_order_no.localeCompare(b.work_order_no), ...getColumnSearchProps('work_order_no') },
        { title: 'Invoice Work Order', dataIndex: 'invoice_work_order_no', key: 'invoice_work_order_no', sorter: (a, b) => a.invoice_work_order_no.localeCompare(b.invoice_work_order_no), ...getColumnSearchProps('invoice_work_order_no') },
        { title: 'Customer', dataIndex: 'customer', key: 'customer', sorter: (a, b) => a.customer.localeCompare(b.customer), ...getColumnSearchProps('customer') },
        { title: 'Chassis No', dataIndex: 'chassis_no', key: 'chassis_no', sorter: (a, b) => a.chassis_no.localeCompare(b.chassis_no), ...getColumnSearchProps('chassis_no') },
    ];
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    return (

        <div id="ihrq" style={transformStyle}>
            <div className="gjs-grid-row" id="izbef">
                <div className="gjs-grid-column" id="idtec">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Invoice Return Work Order</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/invoice_return_work_order",
                                title: "Invoice Return Work Order",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i10ak">
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={isLoading}
                    rowKey="id"
                    bordered
                    size="small"
                    pagination={{ pageSize: 10 }}
                    onRow={(record) => ({
                        onDoubleClick: () => {
                            navigate("/dashboard/invoice_return_work_order", { state: { invoice_return_no: record.invoice_return_no } });
                        },
                        style: { cursor: 'pointer' }
                    })}
                />
            </div>
        </div>
    );
};

export default DataInvoiceReturnWorkOrder;
